import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Globe2, Lock, ShieldCheck, RotateCcw } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAuth, saveSession } from '../contexts/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(0);
  const [resending, setResending] = useState(false);
  const otpRefs = useRef([]);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && user && user.role === 'admin') navigate('/admin/dashboard');
  }, [user, authLoading, navigate]);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      if (data.otp_required) {
        setOtpStep(true);
        setOtpEmail(data.email);
        setCountdown(180);
        setOtp(['', '', '', '', '', '']);
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Giriş uğursuz oldu');
    } finally { setLoading(false); }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
    // Auto-submit when all 6 digits filled
    if (newOtp.every(d => d) && newOtp.join('').length === 6) {
      verifyOtp(newOtp.join(''));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
    if (e.key === 'Enter' && otp.every(d => d)) verifyOtp(otp.join(''));
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      const newOtp = paste.split('');
      setOtp(newOtp);
      otpRefs.current[5]?.focus();
      verifyOtp(paste);
    }
  };

  const verifyOtp = async (code) => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/auth/verify-otp`, { email: otpEmail, code });
      saveSession(data);
      if (data.role === 'admin') navigate('/admin/dashboard');
      else setError('Admin hüquqlarınız yoxdur');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'OTP doğrulama uğursuz');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const resendOtp = async () => {
    setResending(true); setError('');
    try {
      await axios.post(`${API}/api/auth/resend-otp`, { email: otpEmail });
      setCountdown(180);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } catch (err) {
      setError('Yeni kod göndərilə bilmədi');
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-[#03045E] flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="earth-globe-wrapper mx-auto mb-4">
            <div className="earth-globe-tilt">
              <div className="earth-globe-sphere w-14 h-14" style={{backgroundImage: `url(${API}/api/static/uploads/world_map.jpg)`}} />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white">Admin Panel</h1>
          <p className="font-body text-white/60 mt-2 text-sm">Dərsimiz Coğrafiya idarəetmə paneli</p>
        </div>

        {!otpStep ? (
          <form onSubmit={handleLogin} className="bg-white rounded-2xl p-8 shadow-2xl" data-testid="admin-login-form">
            <div className="flex items-center gap-2 mb-6">
              <Lock className="w-5 h-5 text-[#0077B6]" />
              <h2 className="font-body font-semibold text-[#1A2530]">Giriş</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="font-body text-sm text-[#5C6B7A] mb-1 block">E-poçt</label>
                <Input data-testid="admin-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="admin@dersimizcografiya.az" />
              </div>
              <div>
                <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Parol</label>
                <Input data-testid="admin-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Parolunuzu daxil edin" />
              </div>
            </div>
            {error && <p data-testid="admin-login-error" className="text-red-500 text-sm mt-4 font-body">{error}</p>}
            <Button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full mt-6 bg-[#0077B6] hover:bg-[#005f92] font-body py-3 h-auto">
              {loading ? 'Yoxlanılır...' : 'Davam Et'}
            </Button>
          </form>
        ) : (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl p-8 shadow-2xl" data-testid="otp-form">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-5 h-5 text-[#0077B6]" />
              <h2 className="font-body font-semibold text-[#1A2530]">OTP Doğrulama</h2>
            </div>
            <p className="font-body text-sm text-[#5C6B7A] mb-6">
              6 rəqəmli kod <strong>Isfendiyar.Isgenderov@dersimizcografiya.az</strong> adresinə göndərildi.
            </p>

            <div className="flex justify-center gap-2 mb-4" onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input key={i} ref={el => otpRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1}
                  value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)}
                  data-testid={`otp-input-${i}`}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none focus:border-[#0077B6] focus:ring-2 focus:ring-[#0077B6]/20 transition-all" />
              ))}
            </div>

            {/* Countdown */}
            <div className="text-center mb-4">
              {countdown > 0 ? (
                <span className="text-xs text-[#5C6B7A]">Kod etibarlıdır: <strong className="text-[#0077B6]">{Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}</strong></span>
              ) : (
                <span className="text-xs text-red-500 font-semibold">Kodun müddəti bitdi</span>
              )}
            </div>

            {error && <p className="text-red-500 text-sm text-center mb-4 font-body">{error}</p>}

            <Button data-testid="otp-verify-btn" onClick={() => verifyOtp(otp.join(''))} disabled={loading || otp.some(d => !d)}
              className="w-full bg-[#0077B6] hover:bg-[#005f92] font-body py-3 h-auto mb-3">
              {loading ? 'Doğrulanır...' : 'Doğrula'}
            </Button>

            <div className="flex items-center justify-between">
              <button onClick={() => { setOtpStep(false); setError(''); }} className="text-xs text-[#5C6B7A] hover:text-[#1A2530] transition-colors font-body">
                Geriyə qayıt
              </button>
              <button onClick={resendOtp} disabled={resending || countdown > 150} data-testid="otp-resend-btn"
                className="inline-flex items-center gap-1 text-xs text-[#0077B6] hover:text-[#005f92] transition-colors font-body disabled:opacity-40">
                <RotateCcw className="w-3 h-3" /> {resending ? 'Göndərilir...' : 'Yeni kod göndər'}
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
